import {
  queryOptions,
  infiniteQueryOptions,
  mutationOptions,
} from "@tanstack/react-query";
import {
  createMenuTemplate,
  createMenuVariant,
  deleteMenuTemplate,
  deleteMenuVariant,
  fetchCompanyConfiguration,
  fetchDashboardGroups,
  fetchDashboardModuleCompanyRedirectsWidget,
  fetchDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite,
  fetchDashboardModuleConfiguration,
  fetchDefaultLogo,
  fetchMenuTemplate,
  fetchMenuTemplates,
  fetchMenuTemplatesPaged,
  fetchMenuVariant,
  fetchMenuVariants,
  fetchModulesConfiguration,
  updateCompanyConfiguration,
  updateDashboardModuleCompanyRedirects,
  updateDashboardModuleConfiguration,
  updateDefaultLogo,
  updateMenuTemplate,
  updateMenuVariant,
  updateModulesConfiguration,
  uploadDashboardModuleCompanyRedirectsWidgetIcon,
  uploadLogo,
} from "src/Pages/Company/Dashboard/services/api/api";
import {
  CreateMenuTemplatePayload,
  CreateMenuVariantPayload,
  CursorPaginationParams,
  DashboardModuleCompanyRedirectsWidgetPayload,
  DashboardModuleConfigurationPayload,
  GroupType,
  PagePaginationParams,
  UpdateConfigurationPayload,
  UpdateDefaultLogoPayload,
  UpdateMenuTemplatePayload,
  UpdateMenuVariantPayload,
  UpdateModulesConfigurationPayload,
} from "src/Pages/Company/Dashboard/services/api/api.models";

const GlobalKeys = {
  all: () => ["dashboard"],
};

const DEFAULT_STALE_TIME = 5 * 60 * 1000;

const DashboardQueries = {
  getDashboardModuleConfiguration: {
    all: () => [...GlobalKeys.all(), "getDashboardModuleConfiguration"],
    byCompanyId: (enabled: boolean = true, companyId: string) =>
      queryOptions({
        queryKey: [
          ...DashboardQueries.getDashboardModuleConfiguration.all(),
          companyId,
        ],
        queryFn: () => fetchDashboardModuleConfiguration(companyId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getDashboardModuleCompanyRedirectsWidget: {
    all: () => [
      ...GlobalKeys.all(),
      "getDashboardModuleCompanyRedirectsWidget",
    ],
    byCompanyId: (enabled: boolean = true, companyId: string) =>
      queryOptions({
        queryKey: [
          ...DashboardQueries.getDashboardModuleCompanyRedirectsWidget.all(),
          companyId,
        ],
        queryFn: () => fetchDashboardModuleCompanyRedirectsWidget(companyId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite: {
    all: () => [
      ...GlobalKeys.all(),
      "getDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite",
    ],
    companyId: (
      enabled: boolean = true,
      companyId: string,
      payload: { websiteUrl: string }
    ) =>
      queryOptions({
        queryKey: [
          ...DashboardQueries.getDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite.all(),
          companyId,
          payload.websiteUrl,
        ],
        queryFn: () =>
          fetchDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite(
            companyId,
            payload
          ),
        enabled,
      }),
  },
  getGroups: {
    all: () => [...GlobalKeys.all(), "getGroups"],
    byCompanyId: (
      enabled: boolean = true,
      companyId: string,
      groupType: GroupType,
      params?: Omit<CursorPaginationParams, "cursor">
    ) =>
      infiniteQueryOptions({
        queryKey: [
          ...DashboardQueries.getGroups.all(),
          companyId,
          groupType,
          params,
        ],
        queryFn: ({ pageParam }) =>
          fetchDashboardGroups(companyId, groupType, {
            size: 10,
            ...params,
            ...(pageParam ? { cursor: pageParam } : {}),
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getDefaultLogo: {
    all: () => [...GlobalKeys.all(), "getDefaultLogo"],
    query: (enabled: boolean = true) =>
      queryOptions({
        queryKey: DashboardQueries.getDefaultLogo.all(),
        queryFn: () => fetchDefaultLogo(),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getMenuTemplates: {
    all: () => [...GlobalKeys.all(), "getMenuTemplates"],
    list: (enabled: boolean = true, params?: CursorPaginationParams) =>
      queryOptions({
        queryKey: [...DashboardQueries.getMenuTemplates.all(), params],
        queryFn: () => fetchMenuTemplates(params),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getMenuTemplatesPaged: {
    all: () => [...GlobalKeys.all(), "getMenuTemplatesPaged"],
    list: (enabled: boolean = true, params?: PagePaginationParams) =>
      queryOptions({
        queryKey: [...DashboardQueries.getMenuTemplatesPaged.all(), params],
        queryFn: () => fetchMenuTemplatesPaged(params),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getMenuTemplate: {
    all: () => [...GlobalKeys.all(), "getMenuTemplate"],
    byId: (enabled: boolean = true, menuId: string) =>
      queryOptions({
        queryKey: [...DashboardQueries.getMenuTemplate.all(), menuId],
        queryFn: () => fetchMenuTemplate(menuId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getMenuVariants: {
    all: () => [...GlobalKeys.all(), "getMenuVariants"],
    byCompanyId: (
      enabled: boolean = true,
      companyId: string,
      params?: Omit<CursorPaginationParams, "cursor">
    ) =>
      infiniteQueryOptions({
        queryKey: [
          ...DashboardQueries.getMenuVariants.all(),
          companyId,
          params,
        ],
        queryFn: ({ pageParam }) =>
          fetchMenuVariants(companyId, {
            size: 10,
            ...params,
            ...(pageParam ? { cursor: pageParam } : {}),
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getMenuVariant: {
    all: () => [...GlobalKeys.all(), "getMenuVariant"],
    byId: (enabled: boolean = true, companyId: string, menuId: string) =>
      queryOptions({
        queryKey: [...DashboardQueries.getMenuVariant.all(), companyId, menuId],
        queryFn: () => fetchMenuVariant(companyId, menuId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  getCompanyConfiguration: {
    all: () => [...GlobalKeys.all(), "getCompanyConfiguration"],
    byCompanyId: (enabled: boolean = true, companyId: string) =>
      queryOptions({
        queryKey: [
          ...DashboardQueries.getCompanyConfiguration.all(),
          companyId,
        ],
        queryFn: () => fetchCompanyConfiguration(companyId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  updateDashboardModuleConfiguration: (
    companyId: string,
    payload: DashboardModuleConfigurationPayload
  ) =>
    mutationOptions({
      mutationFn: () =>
        updateDashboardModuleConfiguration({ companyId, payload }),
    }),
  uploadCompanyRedirectsWidgetIcon: (companyId: string) =>
    mutationOptions({
      mutationFn: (file: File) =>
        uploadDashboardModuleCompanyRedirectsWidgetIcon(companyId, file),
    }),
  updateCompanyRedirects: (companyId: string) =>
    mutationOptions({
      mutationFn: (payload: DashboardModuleCompanyRedirectsWidgetPayload) =>
        updateDashboardModuleCompanyRedirects({ companyId, payload }),
    }),
  uploadLogo: () =>
    mutationOptions({
      mutationFn: (file: File) => uploadLogo(file),
    }),
  updateDefaultLogo: () =>
    mutationOptions({
      mutationFn: (payload: UpdateDefaultLogoPayload) =>
        updateDefaultLogo(payload),
    }),
  createMenuTemplate: () =>
    mutationOptions({
      mutationFn: (payload: CreateMenuTemplatePayload) =>
        createMenuTemplate(payload),
    }),
  updateMenuTemplate: () =>
    mutationOptions({
      mutationFn: ({
        templateId,
        payload,
      }: {
        templateId: string;
        payload: UpdateMenuTemplatePayload;
      }) => updateMenuTemplate({ templateId, payload }),
    }),
  deleteMenuTemplate: () =>
    mutationOptions({
      mutationFn: (templateId: string) => deleteMenuTemplate(templateId),
    }),
  createMenuVariant: (companyId: string) =>
    mutationOptions({
      mutationFn: (payload: CreateMenuVariantPayload) =>
        createMenuVariant({ companyId, payload }),
    }),
  updateMenuVariant: (companyId: string) =>
    mutationOptions({
      mutationFn: ({
        menuId,
        payload,
      }: {
        menuId: string;
        payload: UpdateMenuVariantPayload;
      }) => updateMenuVariant({ companyId, menuId, payload }),
    }),
  deleteMenuVariant: (companyId: string) =>
    mutationOptions({
      mutationFn: (menuId: string) => deleteMenuVariant({ companyId, menuId }),
    }),
  updateCompanyConfiguration: (companyId: string) =>
    mutationOptions({
      mutationFn: (payload: UpdateConfigurationPayload) =>
        updateCompanyConfiguration({ companyId, payload }),
    }),
  getModulesConfiguration: {
    all: () => [...GlobalKeys.all(), "getModulesConfiguration"],
    byCompanyId: (enabled: boolean = true, companyId: string) =>
      queryOptions({
        queryKey: [
          ...DashboardQueries.getModulesConfiguration.all(),
          companyId,
        ],
        queryFn: () => fetchModulesConfiguration(companyId),
        enabled,
        staleTime: DEFAULT_STALE_TIME,
      }),
  },
  updateModulesConfiguration: (companyId: string) =>
    mutationOptions({
      mutationFn: (payload: UpdateModulesConfigurationPayload) =>
        updateModulesConfiguration({ companyId, payload }),
    }),
};

export default DashboardQueries;
